import React, { useState, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import MariageForm from "../../components/MariageForm";
import MariageList from "../../components/MariageList";
import UserList from "../../components/UserList";
import TableAssignment from "../../components/TableAssignment";
import EvenementTableAssignment from "../../components/EvenementTableAssignment";
import AnniversaireTableAssignment from "../../components/AnniversaireTableAssignment";
import MegaAdminPanel from "../../components/MegaAdminPanel";
import LogsList from "../../components/LogsList";
import EvenementForm from "../../components/EvenementForm";
import AnniversaireForm from "../../components/AnniversaireForm";
import EvenementList from "../../components/EvenementList";
import AnniversaireList from "../../components/AnniversaireList";
import api from "../../services/api";
import "./cssDash/MegaAdminDashboard.css"; 

// Interfaces pour les réponses API
interface TablesResponse {
  success: boolean;
  tables: string[];
  counts?: Record<string, number>;
  totalInvites?: number;
  assignments?: Record<string, string>;
}

interface AssignmentsResponse {
  success: boolean;
  assignments: Record<string, string>;
}

interface ProtocolesResponse {
  success: boolean;
  protocoles: Array<{
    _id: string;
    name: string;
    phone: string;
    province: string;
    tablesAssignees: number;
  }>;
}

const MegaAdminDashboard = () => {
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState("mariages");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedMariageId, setSelectedMariageId] = useState<string | null>(null);
  const [selectedEvenementId, setSelectedEvenementId] = useState<string | null>(null);
  const [selectedAnniversaireId, setSelectedAnniversaireId] = useState<string | null>(null);
  
  // États pour stocker les données des événements
  const [evenementTables, setEvenementTables] = useState<string[]>([]);
  const [evenementAssignments, setEvenementAssignments] = useState<Record<string, string>>({});
  const [evenementProtocoles, setEvenementProtocoles] = useState<any[]>([]);
  
  // États pour stocker les données des anniversaires
  const [anniversaireTables, setAnniversaireTables] = useState<string[]>([]);
  const [anniversaireAssignments, setAnniversaireAssignments] = useState<Record<string, string>>({});
  const [anniversaireProtocoles, setAnniversaireProtocoles] = useState<any[]>([]);

  const handleSelectMariage = (id: string) => {
    setSelectedMariageId(id);
    setActiveTab("assign-mariage");
  };

  const handleSelectEvenement = useCallback(async (id: string) => {
    setSelectedEvenementId(id);
    setActiveTab("assign-evenement");
    
    try {
      const [tablesRes, assignmentsRes, protocolesRes] = await Promise.all([
        api.get<TablesResponse>(`/evenements/${id}/tables`),
        api.get<AssignmentsResponse>(`/evenements/${id}/assignments`),
        api.get<ProtocolesResponse>(`/evenements/${id}/protocoles-disponibles`)
      ]);
      
      setEvenementTables(tablesRes.data.tables || []);
      setEvenementAssignments(assignmentsRes.data.assignments || {});
      setEvenementProtocoles(protocolesRes.data.protocoles || []);
    } catch (error) {
      console.error("Erreur chargement données événement:", error);
      setEvenementTables([]);
      setEvenementAssignments({});
      setEvenementProtocoles([]);
    }
  }, []);

  const handleSelectAnniversaire = useCallback(async (id: string) => {
    setSelectedAnniversaireId(id);
    setActiveTab("assign-anniversaire");
    
    try {
      const [tablesRes, assignmentsRes, protocolesRes] = await Promise.all([
        api.get<TablesResponse>(`/anniversaires/${id}/tables`),
        api.get<AssignmentsResponse>(`/anniversaires/${id}/assignments`),
        api.get<ProtocolesResponse>(`/anniversaires/${id}/protocoles-disponibles`)
      ]);
      
      setAnniversaireTables(tablesRes.data.tables || []);
      setAnniversaireAssignments(assignmentsRes.data.assignments || {});
      setAnniversaireProtocoles(protocolesRes.data.protocoles || []);
    } catch (error) {
      console.error("Erreur chargement données anniversaire:", error);
      setAnniversaireTables([]);
      setAnniversaireAssignments({});
      setAnniversaireProtocoles([]);
    }
  }, []);

  const refreshEvenementData = useCallback(async () => {
    if (!selectedEvenementId) return;
    try {
      const [tablesRes, assignmentsRes, protocolesRes] = await Promise.all([
        api.get<TablesResponse>(`/evenements/${selectedEvenementId}/tables`),
        api.get<AssignmentsResponse>(`/evenements/${selectedEvenementId}/assignments`),
        api.get<ProtocolesResponse>(`/evenements/${selectedEvenementId}/protocoles-disponibles`)
      ]);
      
      setEvenementTables(tablesRes.data.tables || []);
      setEvenementAssignments(assignmentsRes.data.assignments || {});
      setEvenementProtocoles(protocolesRes.data.protocoles || []);
    } catch (error) {
      console.error("Erreur rafraîchissement données événement:", error);
    }
  }, [selectedEvenementId]);

  const refreshAnniversaireData = useCallback(async () => {
    if (!selectedAnniversaireId) return;
    try {
      const [tablesRes, assignmentsRes, protocolesRes] = await Promise.all([
        api.get<TablesResponse>(`/anniversaires/${selectedAnniversaireId}/tables`),
        api.get<AssignmentsResponse>(`/anniversaires/${selectedAnniversaireId}/assignments`),
        api.get<ProtocolesResponse>(`/anniversaires/${selectedAnniversaireId}/protocoles-disponibles`)
      ]);
      
      setAnniversaireTables(tablesRes.data.tables || []);
      setAnniversaireAssignments(assignmentsRes.data.assignments || {});
      setAnniversaireProtocoles(protocolesRes.data.protocoles || []);
    } catch (error) {
      console.error("Erreur rafraîchissement données anniversaire:", error);
    }
  }, [selectedAnniversaireId]);

  return (
    <div className="dashboard-container mega-dashboard">
      <button 
        className="hamburger" 
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label="Menu"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" fill="currentColor"/>
        </svg>
      </button>

      <aside className={`sidebar ${sidebarOpen ? "open" : "closed"}`}>
        <div className="sidebar-content">
          <div className="sidebar-header">
            <h2 className="sidebar-title">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" fill="currentColor"/>
              </svg>
              Mega Admin
            </h2>
            <span className="badge-mega">👑</span>
          </div>
          
          <nav className="sidebar-nav">
            <ul>
              {/* Section Mariages */}
              <li className="nav-section">MARIAGES</li>
              <li 
                className={activeTab === "mariages" ? "active" : ""} 
                onClick={() => setActiveTab("mariages")}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 9V7c0-1.1-.9-2-2-2h-4c0-1.66-1.34-3-3-3S8 3.34 8 5H4c-1.1 0-2 .9-2 2v2c0 1.1.9 2 2 2h4c0 1.66 1.34 3 3 3s3-1.34 3-3h4c1.1 0 2-.9 2-2z" fill="currentColor"/>
                </svg>
                Liste des mariages
              </li>
              
              <li 
                className={activeTab === "create-mariage" ? "active" : ""} 
                onClick={() => setActiveTab("create-mariage")}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
                </svg>
                Créer un mariage
              </li>

              <li 
                className={activeTab === "assign-mariage" ? "active" : ""} 
                onClick={() => setActiveTab("assign-mariage")}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H4V5h16v14zM8 10h8v2H8v-2z" fill="currentColor"/>
                </svg>
                Assigner tables (mariages)
              </li>

              {/* Section Événements */}
              <li className="nav-section">ÉVÉNEMENTS</li>
              <li 
                className={activeTab === "evenements" ? "active" : ""} 
                onClick={() => setActiveTab("evenements")}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" fill="currentColor"/>
                </svg>
                Tous les événements
              </li>
              
              <li 
                className={activeTab === "create-evenement" ? "active" : ""} 
                onClick={() => setActiveTab("create-evenement")}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
                </svg>
                Créer un événement
              </li>

              <li 
                className={activeTab === "assign-evenement" ? "active" : ""} 
                onClick={() => setActiveTab("assign-evenement")}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H4V5h16v14zM8 10h8v2H8v-2z" fill="currentColor"/>
                </svg>
                Assigner tables (événements)
              </li>

              {/* Section Anniversaires */}
              <li className="nav-section">ANNIVERSAIRES</li>
              <li 
                className={activeTab === "anniversaires" ? "active" : ""} 
                onClick={() => setActiveTab("anniversaires")}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 6c1.11 0 2-.9 2-2 0-.38-.1-.73-.29-1.03L12 0l-1.71 2.97c-.19.3-.29.65-.29 1.03 0 1.1.9 2 2 2zm4.6 4.47l-1.07-1.86L13 11.6V17h2v-4.4l1.6-2.13zM17 22v-2h-4v-4h-2v4H7v2h10zM8 12.4L5.6 9.6 4 11.6V17h2v-4.4l2-2.2z" fill="currentColor"/>
                </svg>
                Liste des anniversaires
              </li>
              
              <li 
                className={activeTab === "create-anniversaire" ? "active" : ""} 
                onClick={() => setActiveTab("create-anniversaire")}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
                </svg>
                Créer un anniversaire
              </li>

              <li 
                className={activeTab === "assign-anniversaire" ? "active" : ""} 
                onClick={() => setActiveTab("assign-anniversaire")}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H4V5h16v14zM8 10h8v2H8v-2z" fill="currentColor"/>
                </svg>
                Assigner tables (anniversaires)
              </li>

              {/* Section Administration */}
              <li className="nav-section">ADMINISTRATION</li>
              <li 
                className={activeTab === "users" ? "active" : ""} 
                onClick={() => setActiveTab("users")}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-1 .05 1.16.84 2 1.87 2 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/>
                </svg>
                Utilisateurs
              </li>

              <li 
                className={activeTab === "logs" ? "active" : ""} 
                onClick={() => setActiveTab("logs")}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zM8 10h8v2H8v-2z" fill="currentColor"/>
                </svg>
                Logs
              </li>
              
              <li 
                className={activeTab === "mega" ? "active" : ""} 
                onClick={() => setActiveTab("mega")}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z" fill="currentColor"/>
                </svg>
                Supervision
                <span className="badge">👑</span>
              </li>
            </ul>
          </nav>
          
          <div className="logout-btn-container">
            <button className="logout-btn" onClick={logout}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5-5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" fill="currentColor"/>
              </svg>
              Déconnexion
            </button>
          </div>
        </div>
      </aside>

      <main className="main-content">
        {/* Section Mariages */}
        {activeTab === "mariages" && (
          <MariageList 
            onSelectMariage={handleSelectMariage} 
            readOnly={false}
            canDelete={true}
          />
        )}
        
        {activeTab === "create-mariage" && (
          <div className="tab-content">
            <div className="content-header">
              <h2>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
                </svg>
                Créer un nouveau mariage
              </h2>
            </div>
            <MariageForm />
          </div>
        )}

        {activeTab === "assign-mariage" && selectedMariageId && (
          <div className="tab-content">
            <div className="content-header">
              <h2>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H4V5h16v14zM8 10h8v2H8v-2z" fill="currentColor"/>
                </svg>
                Assignation des tables - Mariage
              </h2>
              <button className="back-btn" onClick={() => setActiveTab("mariages")}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor"/>
                </svg>
                Retour à la liste
              </button>
            </div>
            <TableAssignment mariageId={selectedMariageId} />
          </div>
        )}

        {/* Section Événements */}
        {activeTab === "evenements" && (
          <div className="tab-content">
            <div className="content-header">
              <h2>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" fill="currentColor"/>
                </svg>
                Tous les événements
              </h2>
            </div>
            <EvenementList onSelectEvenement={handleSelectEvenement} />
          </div>
        )}
        
        {activeTab === "create-evenement" && (
          <div className="tab-content">
            <div className="content-header">
              <h2>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
                </svg>
                Créer un événement
              </h2>
            </div>
            <EvenementForm />
          </div>
        )}

        {activeTab === "assign-evenement" && selectedEvenementId && (
          <div className="tab-content">
            <div className="content-header">
              <h2>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H4V5h16v14zM8 10h8v2H8v-2z" fill="currentColor"/>
                </svg>
                Assignation des tables - Événement
              </h2>
              <button className="back-btn" onClick={() => setActiveTab("evenements")}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor"/>
                </svg>
                Retour à la liste
              </button>
            </div>
            <EvenementTableAssignment 
              evenementId={selectedEvenementId}
              tables={evenementTables}
              assignments={evenementAssignments}
              protocoles={evenementProtocoles}
              onUpdate={refreshEvenementData}
            />
          </div>
        )}

        {/* Section Anniversaires */}
        {activeTab === "anniversaires" && (
          <div className="tab-content">
            <div className="content-header">
              <h2>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 6c1.11 0 2-.9 2-2 0-.38-.1-.73-.29-1.03L12 0l-1.71 2.97c-.19.3-.29.65-.29 1.03 0 1.1.9 2 2 2zm4.6 4.47l-1.07-1.86L13 11.6V17h2v-4.4l1.6-2.13zM17 22v-2h-4v-4h-2v4H7v2h10zM8 12.4L5.6 9.6 4 11.6V17h2v-4.4l2-2.2z" fill="currentColor"/>
                </svg>
                Liste des anniversaires
              </h2>
            </div>
            <AnniversaireList onSelectAnniversaire={handleSelectAnniversaire} />
          </div>
        )}
        
        {activeTab === "create-anniversaire" && (
          <div className="tab-content">
            <div className="content-header">
              <h2>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
                </svg>
                Créer un anniversaire
              </h2>
            </div>
            <AnniversaireForm />
          </div>
        )}

        {activeTab === "assign-anniversaire" && selectedAnniversaireId && (
          <div className="tab-content">
            <div className="content-header">
              <h2>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H4V5h16v14zM8 10h8v2H8v-2z" fill="currentColor"/>
                </svg>
                Assignation des tables - Anniversaire
              </h2>
              <button className="back-btn" onClick={() => setActiveTab("anniversaires")}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor"/>
                </svg>
                Retour à la liste
              </button>
            </div>
            <AnniversaireTableAssignment 
              anniversaireId={selectedAnniversaireId}
              tables={anniversaireTables}
              assignments={anniversaireAssignments}
              protocoles={anniversaireProtocoles}
              onUpdate={refreshAnniversaireData}
            />
          </div>
        )}

        {/* Section Administration */}
        {activeTab === "users" && (
          <div className="tab-content">
            <div className="content-header">
              <h2>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-1 .05 1.16.84 2 1.87 2 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/>
                </svg>
                Gestion des utilisateurs
              </h2>
            </div>
            <UserList />
          </div>
        )}

        {activeTab === "logs" && (
          <div className="tab-content">
            <div className="content-header">
              <h2>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zM8 10h8v2H8v-2z" fill="currentColor"/>
                </svg>
                Historique des connexions
              </h2>
            </div>
            <LogsList />
          </div>
        )}
        
        {activeTab === "mega" && (
          <div className="tab-content">
            <div className="content-header">
              <h2>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z" fill="currentColor"/>
                </svg>
                Panneau de supervision
              </h2>
            </div>
            <MegaAdminPanel />
          </div>
        )}
      </main>
    </div>
  );
};

export default MegaAdminDashboard;